import React, { useEffect, useReducer, useState } from 'react'

import {
  BotMitigationPart,
  ClickceaseDomainMigrationPart,
  Domain,
  DomainManagementRowStyled,
  PaidMarketingPart,
  TagManagementPart,
  MultipleElementsWrapper,
} from 'Features/domainsManagement/components/DomainManagmentRow/DomainManagementRow.styled'

import { ExtraOptionPopOver } from 'Features/domainsManagement/components/ExtraOptionPopOver/ExtraOptionPopOver'
import { PaidMarketingButton } from 'Features/domainsManagement/components/DomainManagmentRow/PaidMarketingButton'

import { Domain as DomainType } from 'Types/Domain'
import { PlatformConnectionModal } from 'Features/platformConnectionModal/PlatformConnectionModal'
import { BzSetupPopup } from 'Features/bzSetupPopup/BzSetupPopup'
import { BotMitigationButton } from 'Features/domainsManagement/components/DomainManagmentRow/BotMitigationButton'
import { useGetPackagingLimits } from 'Features/accountSettings/api/getPackagingLimits'
import { useSetBzMonitorMode } from 'Features/domainsManagement/api/updateBzMonitorMode'
import { useAppStore } from 'Stores/appStoreModuleFed'
import { useTranslation } from 'react-i18next'
import { queryClient } from 'Lib/react-query'
import { useGetMigrationMonitorMode } from 'Features/domainsManagement/api/getMigrationMonitorMode'
import { ClickceaseMigration } from '../ClickceaseMigration/ClickceaseMigration'
import { useMigrationDisplay } from 'Hooks/useMigrationDisplay'
import { GLOBAL_MODALS } from 'Types/GlobalModal'
import { Setup } from 'Features/domainsManagement/components/Setup/Setup'
import { TagStatusButton } from 'Features/domainsManagement/components/DomainManagmentRow/ConnectedButtonWithEdit/ConnectedButtonWithEdit'
import { useUserStore } from 'Stores/userStoreModuleFed'

type DomainManagementRowProps = {
  domain: DomainType
  shouldShowExtraOptionsModal: boolean
  setExtraModalOn: (domainId: number) => void
  forceOpenGetTagModal?: boolean
}

export const MODALS_INIT_STATE = {
  showConfirmationModal: false,
  showDomainProductModal: false,
  showGetTagModal: false,
  showCqParamsModal: false,
}

export const DomainManagementRow = ({
  domain,
  shouldShowExtraOptionsModal,
  setExtraModalOn,
  forceOpenGetTagModal,
}: DomainManagementRowProps) => {
  const { t } = useTranslation('domainManagement')
  const [modalToShow, setModalToShow] = useState<string>('')
  const { data: packagesData } = useGetPackagingLimits()
  const { isMigratedAccount } = useMigrationDisplay()
  const { data: migrationDomainsList } = useGetMigrationMonitorMode(isMigratedAccount)
  const { showToaster } = useAppStore()
  const { owner } = useUserStore()

  const modalsReducer = (
    state: typeof MODALS_INIT_STATE,
    action: { type: keyof typeof MODALS_INIT_STATE; payload: boolean }
  ): typeof MODALS_INIT_STATE => {
    const { type, payload } = action

    switch (type) {
      case 'showConfirmationModal':
        return { ...state, showConfirmationModal: payload }
      case 'showDomainProductModal':
        return { ...state, showDomainProductModal: payload }
      case 'showCqParamsModal':
        return { ...state, showCqParamsModal: payload }
      case 'showGetTagModal':
        return { ...state, showGetTagModal: payload }
      default:
        return state
    }
  }

  useEffect(() => {
    if (forceOpenGetTagModal) {
      openTagModal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpenGetTagModal])

  const [state, modalsDispatch] = useReducer(modalsReducer, MODALS_INIT_STATE)

  const { mutate: setBzMonitorMode } = useSetBzMonitorMode()
  const updateDomainBzMonitorMode = ({ tagId, isBzMonitor, address }: DomainType) => {
    setBzMonitorMode(
      { tagId, isBzMonitor: !isBzMonitor },
      {
        onSuccess: async () => {
          showToaster(
            t('toggle_bot_mitigation_monitor_mode.success', { domain: address }),
            'success'
          )
          await queryClient.invalidateQueries(['tagsListPerNetworkId'])
        },
        onError: () =>
          showToaster(
            t('toggle_bot_mitigation_monitor_mode.failure', { domain: address }),
            'failure'
          ),
      }
    )
  }

  const clickceaseMigrationPart = () => {
    if (!migrationDomainsList?.length) return null
    const isDomainMigrated = migrationDomainsList?.some(
      (migratedDomain) => migratedDomain.address === domain.address
    )

    return (
      <ClickceaseDomainMigrationPart>
        {isDomainMigrated ? <ClickceaseMigration domainAddress={domain.address} /> : ''}
      </ClickceaseDomainMigrationPart>
    )
  }

  function openTagModal() {
    setExtraModalOn(domain.id)
    modalsDispatch({ type: 'showGetTagModal', payload: true })
  }

  const renderTagManagementPart = () => {
    if (domain?.tagInstalled) {
      return (
        <TagStatusButton
          text={t('connected')}
          onClick={() => {
            openTagModal()
          }}
        />
      )
    } else if (domain?.gtmImplemented !== null && !domain?.tagInstalled) {
      return (
        <TagStatusButton
          text={t('tag_pending')}
          onClick={() => {
            openTagModal()
          }}
        />
      )
    } else {
      return (
        <Setup
          onClick={() => {
            openTagModal()
          }}
          disabled={!owner}
        />
      )
    }
  }

  return (
    <DomainManagementRowStyled>
      <Domain>{domain.address}</Domain>
      <TagManagementPart>{renderTagManagementPart()}</TagManagementPart>
      {clickceaseMigrationPart()}
      {packagesData?.paidMarketingAllowed && (
        <PaidMarketingPart>
          <MultipleElementsWrapper>
            <PaidMarketingButton
              domain={domain}
              openPlatformConnectionModal={() => setModalToShow(GLOBAL_MODALS.PLATFORM_CONNECTION)}
            />
          </MultipleElementsWrapper>
        </PaidMarketingPart>
      )}
      {packagesData?.botZappingAllowed && (
        <BotMitigationPart>
          <MultipleElementsWrapper>
            <BotMitigationButton
              domain={domain}
              openBotMitigationModal={() => setModalToShow(GLOBAL_MODALS.BM_SETUP)}
              updateDomainBzMonitorMode={() => updateDomainBzMonitorMode(domain)}
              disabled={!owner}
            />
          </MultipleElementsWrapper>
        </BotMitigationPart>
      )}
      <ExtraOptionPopOver
        onOpenChange={(open) => setExtraModalOn(open ? domain.id : 0)}
        isOpen={shouldShowExtraOptionsModal}
        domain={domain}
        state={state}
        modalsDispatch={modalsDispatch}
      />
      <PlatformConnectionModal
        onOpenChange={() => setModalToShow('')}
        isOpen={modalToShow === GLOBAL_MODALS.PLATFORM_CONNECTION}
      />
      <BzSetupPopup
        isOpen={modalToShow === GLOBAL_MODALS.BM_SETUP}
        onOpenChange={() => setModalToShow('')}
        domainAddress={domain.address}
      />
    </DomainManagementRowStyled>
  )
}
